import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/Card';
import FilterBar from '../analytics/FilterBar';
import BasketsSection from '../../pages/analytics/BasketsSection';
import brokerService from '../../services/brokerService';
import type { AnalyticsFilters } from '../../types/analytics';

interface Props {
  basketId: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Performance tab on BasketsPage — wraps the analytics Baskets section in
 * its own filter scope. Defaults to last 30 days, the current basket, and
 * all accounts; FilterBar lets the user widen the date range or narrow to
 * one broker account. Basket / strategy dropdowns are intentionally hidden
 * (basket is fixed by tab context, strategies stay empty like the analytics
 * route does).
 */
const BasketPerformanceTab: React.FC<Props> = ({ basketId }) => {
  const [filters, setFilters] = useState<AnalyticsFilters>(() => ({
    start: daysAgoIso(29),
    end: todayIso(),
    accounts: [],
    baskets: [basketId],
    strategies: [],
  }));
  const [accounts, setAccounts] = useState<Array<{ broker: string; clientId: string; label: string }>>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // Keep filters.baskets pinned to the active basket when the user switches
  // between baskets inside TabbedBasketManager without unmounting this tab.
  useEffect(() => {
    setFilters(f => (f.baskets?.[0] === basketId ? f : { ...f, baskets: [basketId] }));
  }, [basketId]);

  useEffect(() => {
    (async () => {
      try {
        const accs = await brokerService.getBrokerAccounts();
        setAccounts(accs.map((a: any) => ({
          broker: a.broker_name,
          clientId: a.client_id,
          label: `${a.broker_name} · ${a.client_id}`,
        })));
      } catch { /* non-fatal — account filter just stays hidden */ }
    })();
  }, []);

  const handleRefresh = useCallback(() => {
    // BasketsSection re-fetches when its `filters` prop reference changes;
    // bump a tick into a fresh filters object to retrigger without changing
    // the date/account selection.
    setFilters(f => ({ ...f }));
    setRefreshTick(t => t + 1);
  }, []);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            onRefresh={handleRefresh}
            accounts={accounts}
            baskets={[]}
            strategies={[]}
          />
        </CardContent>
      </Card>
      <BasketsSection key={refreshTick} filters={filters} />
    </div>
  );
};

export default BasketPerformanceTab;
